import React, { Fragment, useState, useEffect, useRef } from 'react'
import { oneOfType, arrayOf, bool, func, shape, node } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { selectLabels } from '../../redux/configuration/selectors'

import {
  setDepartureRoute,
  setReturnRoute,
  setForcesBooking
} from '../../redux/ferryRouteSelections/actions'
import {
  selectDepartureDate,
  selectDepartureRoute,
  selectForcesBooking
} from '../../redux/ferryRouteSelections/selectors'
import {
  clearPromoValidation,
  fetchPromoInformation
} from '../../redux/promo/actions'
import * as UserSelectionActions from '../../redux/userSelections/actions'
import { selectCurrentBookingPromoState } from '../../redux/selectors/selectCurrentBookingPromoState'
import FerryRoutesFormSelect from './elements/FerryRoutesFormSelect'
import FerryRoutesFormSingleDatePicker from './elements/dates/FerryRoutesFormSingleDatePicker'
import ValidatedPromoInput from '../ValidatedPromoInput/ValidatedPromoInput'
import WarningBanner from '../WarningBanner/WarningBanner'
import Checkbox from '../Checkbox/Checkbox'
import ModalForm from '../ModalForm'
import AcknowledgmentModal from '../AcknowledgmentModal/AcknowledgmentModal'

const FerryRoutesOneWayForm = ({ availabilityErrors, ferryRoutes, submit }) => {
  const [showModal, setShowModal] = useState(false)
  const [scrollButtom, setScrollButtom] = useState(false)
  const dispatch = useDispatch()
  const activeDepartureRoute = useSelector(selectDepartureRoute)
  const departureDate = useSelector(selectDepartureDate)
  const labels = useSelector(selectLabels)
  const promoInputState = useSelector(selectCurrentBookingPromoState)
  const forcesBooking = useSelector(selectForcesBooking)
  const labelRef = useRef(null)

  const handleCheckBoxClick = event => {
    const isChecked = event.target.checked
    if (!isChecked) {
      dispatch(setForcesBooking(false))
      dispatch(UserSelectionActions.setIsForcesMember(false))
    } else {
      setShowModal(true)
    }
  }

  const handleScroll = event => {
    const el = event.target
    const scrollHeightThreshold = 10
    const atBottom =
      el.scrollHeight - el.scrollTop <= el.clientHeight + scrollHeightThreshold
    setScrollButtom(atBottom)
  }

  const handleAcknowledge = () => {
    setShowModal(false)
    dispatch(setForcesBooking(true))
    dispatch(UserSelectionActions.setIsForcesMember(true))
  }

  let isSubmitEnabled = false
  useEffect(() => {
    const link = labelRef?.current?.querySelector('a')
    if (link) {
      link.setAttribute(
        'aria-label',
        'Canadian Forces eligibility requirements'
      )
    }
  }, [labelRef, forcesBooking])

  if (
    activeDepartureRoute &&
    activeDepartureRoute.code &&
    departureDate &&
    (promoInputState.valid === true ||
      promoInputState.valid === null ||
      promoInputState.code === '')
  ) {
    isSubmitEnabled = true
  }
  if (promoInputState.valid && forcesBooking) {
    isSubmitEnabled = false
  }

  const handleDepartureRoute = routeData => {
    dispatch(setDepartureRoute(routeData)) // TODO: Deprecate
    dispatch(setReturnRoute({})) // TODO: Deprecate

    dispatch(UserSelectionActions.setDepartureRoute(routeData.code))
    dispatch(UserSelectionActions.setReturnRoute(null))
  }

  return (
    <div className="ferry-routes__form-one-way">
      {!isEmpty(ferryRoutes) && (
        <div className="ferry-routes__form-routes">
          <FerryRoutesFormSelect
            activeRoute={activeDepartureRoute}
            ferryRoutes={ferryRoutes}
            fieldLabel={labels.departureRoute}
            toggleLabel={labels.change}
            updateRouteEvent={handleDepartureRoute}
          />
        </div>
      )}

      <FerryRoutesFormSingleDatePicker
        ferryLabels={labels}
        onChange={() => dispatch(clearPromoValidation())}
      />

      <div className="ferry-routes__validated-promo-container">
        <ValidatedPromoInput
          labels={labels}
          handleBlur={v => dispatch(UserSelectionActions.setPromoCode(v))}
          submitValidation={code => {
            return dispatch(fetchPromoInformation(code)).then(isValid => {
              return isValid
            })
          }}
          clearValidation={() => dispatch(clearPromoValidation())}
          {...promoInputState}
        />
      </div>

      <div className="ferry-routes__forces-input-container">
        <Checkbox
          name={'canadianMilitary'}
          value={forcesBooking}
          label={labels.canadianForcesMember}
          disabled={false}
          onChange={handleCheckBoxClick}
          formikCheckbox={false}
        />
      </div>
      {forcesBooking && labels.canadianForcesMemberElig1 && (
        <p
          className="cfid-disclaimer"
          ref={labelRef}
          dangerouslySetInnerHTML={{ __html: labels.canadianForcesMemberElig1 }}
        ></p>
      )}
      {showModal && (
        <ModalForm
          modalClasses={'modal--smed'}
          isOpen={showModal}
          closeModal={() => setShowModal(false)}
          formTitle={' '}
        >
          <AcknowledgmentModal
            handleScroll={handleScroll}
            labels={labels}
            handleAcknowledge={handleAcknowledge}
            scrollButtom={scrollButtom}
          />
        </ModalForm>
      )}

      {availabilityErrors?.length ? (
        <Fragment>
          {availabilityErrors.map((error, i) => (
            <WarningBanner key={error + i}>{error}</WarningBanner>
          ))}
        </Fragment>
      ) : null}

      {promoInputState.valid && forcesBooking && (
        <WarningBanner>{labels.discountCannotBeCombined}</WarningBanner>
      )}
      <button
        className="ferry-routes__form-submit btn btn-primary large-primary-btn"
        onClick={submit}
        disabled={!isSubmitEnabled}
        data-test="btn-submit"
      >
        {labels.continueToPassengerInfoBtn} →
      </button>
    </div>
  )
}

FerryRoutesOneWayForm.propTypes = {
  availabilityErrors: oneOfType([bool, arrayOf(node)]),
  openModal: func.isRequired,
  closeModal: func.isRequired,
  modalIsOpen: shape().isRequired,
  ferryRoutes: arrayOf(shape()).isRequired,
  isPairRoutes: bool,
  submit: func.isRequired
}

export default FerryRoutesOneWayForm
